/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
    HardwareHealthNotificationSubscriptionCreateRequest,
    HardwareHealthNotificationTriggerRequestChild,
    HardwareNotificationSubscriptionCreateRequest,
    HardwareNotificationTriggerRequestChild,
    IHardwareHealthNotificationSubscriptionCreateRequest,
    IHardwareNotificationSubscriptionCreateRequest,
    IIndicatorNotificationSubscriptionCreateRequest,
    IMachineHealthNotificationSubscriptionCreateRequest,
    IndicatorNotificationSubscriptionCreateRequest,
    IndicatorNotificationTriggerRequestChild,
    MachineHealthNotificationSubscriptionCreateRequest,
    MachineHealthNotificationTriggerRequestChild,
    NotificationType,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { NotificationSubscription } from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import {
    CreateHardwareHealthNotificationSubscriptionFormData,
    CreateHardwareNotificationSubscriptionFormData,
    CreateIndicatorAlertsNotificationSubscriptionFormData,
    CreateMachineHealthNotificationSubscriptionFormData,
    CreateNotificationSubscriptionFormData,
} from 'pages/Settings/NotificationSubscriptions/form/types';
import { objectEntries } from 'utils/object';
import { TimeSpan } from 'utils/TimeSpan';

async function createIndicatorAlertsNotificationSubscriptions(
    formData: CreateIndicatorAlertsNotificationSubscriptionFormData
) {
    const promises = formData.users
        .map((u) => u.Id)
        .map<IIndicatorNotificationSubscriptionCreateRequest>((userId) => ({
            IsSilenced: false,
            LocationId: formData.location!.Id,
            GroupId: formData.groupId,
            UserId: userId,
            NotificationTriggers: Object.values(formData.notificationTriggers)
                .filter((value) => value.IsEmailEnabled || value.IsSmsEnabled)
                .map((trigger) => new IndicatorNotificationTriggerRequestChild(trigger)),
            UnacknowledgedPeriod: TimeSpan.fromMillis(
                formData.unacknowledgedPeriodMs
            ).toDurationString(),
        }))
        .map((request) =>
            sdClient.createIndicatorNotificationSubscription({
                body: new IndicatorNotificationSubscriptionCreateRequest(request),
            })
        );

    return Promise.all(promises);
}

export function createHardwareNotificationSubscriptions(
    formData: CreateHardwareNotificationSubscriptionFormData
) {
    const promises = formData.users
        .map((u) => u.Id)
        .map<IHardwareNotificationSubscriptionCreateRequest>((userId) => ({
            IsSilenced: false,
            LocationId: formData.location!.Id,
            UserId: userId,
            NotificationTriggers: Object.values(formData.notificationTriggers)
                .filter((value) => value.IsEmailEnabled || value.IsSmsEnabled)
                .map((trigger) => new HardwareNotificationTriggerRequestChild(trigger)),
        }))
        .map((request) =>
            sdClient.createHardwareNotificationSubscription({
                body: new HardwareNotificationSubscriptionCreateRequest(request),
            })
        );

    return Promise.all(promises);
}

async function createMachineHealthNotificationSubscriptions(
    formData: CreateMachineHealthNotificationSubscriptionFormData
) {
    const promises = formData.users
        .map((u) => u.Id)
        .map<IMachineHealthNotificationSubscriptionCreateRequest>((userId) => ({
            IsSilenced: false,
            LocationId: formData.location!.Id,
            GroupId: formData.groupId,
            UserId: userId,
            NotificationTriggers: objectEntries(formData.notificationTriggers)
                .filter(
                    ([_key, value]) =>
                        value.IsEmailEnabled || value.IsSdConnectPushEnabled
                )
                .map(
                    ([key, trigger]) =>
                        new MachineHealthNotificationTriggerRequestChild({
                            ...trigger,
                            MachineHealthEventType: key,
                        })
                ),
        }))
        .map((request) =>
            sdClient.createMachineHealthNotificationSubscription({
                body: new MachineHealthNotificationSubscriptionCreateRequest(request),
            })
        );

    return Promise.all(promises);
}

export function createHardwareHealthNotificationSubscriptions(
    formData: CreateHardwareHealthNotificationSubscriptionFormData
) {
    const promises = formData.users
        .map((u) => u.Id)
        .map<IHardwareHealthNotificationSubscriptionCreateRequest>((userId) => ({
            IsSilenced: false,
            LocationId: formData.location!.Id,
            UserId: userId,
            NotificationTriggers: formData.faultTypes.map(
                (faultType) =>
                    new HardwareHealthNotificationTriggerRequestChild({
                        FaultTypeId: faultType.id,
                        IsEmailEnabled: true,
                    })
            ),
        }))
        .map((request) =>
            sdClient.createNotificationSubscription({
                body: new HardwareHealthNotificationSubscriptionCreateRequest(request),
            })
        );

    return Promise.all(promises);
}

function createNotificationSubscription(
    formData: CreateNotificationSubscriptionFormData
): Promise<NotificationSubscription[]> {
    switch (formData.type) {
        case NotificationType.Indicator:
            return createIndicatorAlertsNotificationSubscriptions({
                ...formData,
                ...formData.indicatorAlerts!,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any;
        case NotificationType.Hardware:
            return createHardwareNotificationSubscriptions({
                ...formData,
                ...formData.hardware!,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any;
        case NotificationType.MachineHealth:
            return createMachineHealthNotificationSubscriptions({
                ...formData,
                ...formData.machineHealth!,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any;
        case NotificationType.HardwareHealth:
            return createHardwareHealthNotificationSubscriptions({
                ...formData,
                ...formData.hardwareIssues!,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any;
        default:
            throw new Error(
                `Received an unexpected formData NotificationType: ${formData.type}`
            );
    }
}

/** Type of data returned by the mutation function */
type Data = NotificationSubscription[];

/** Type of variables which can be passed into the mutate function */
type Variables = CreateNotificationSubscriptionFormData;

/** The mutation options object type */
type Options = UseMutationOptions<Data, unknown, Variables>;

export function useCreateNotificationSubscriptions(options: Options) {
    return useMutation<Data, unknown, Variables>({
        mutationFn: (data) => createNotificationSubscription(data),
        ...options,
    });
}
